import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { JsonEditor } from '../../_shared/components/JsonEditor';
import { certificateFormSchema } from '../schema/certificateSchema';
import { useCreateCertificate, useReplaceCertificate } from '../hooks/useCertificates';
import type { CertificateFormValues } from '../schema/certificateSchema';
import type { CertificateRow } from '../types/certificate';

interface CertificateFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: CertificateRow | null;
  onClose: () => void;
  onSaved?: () => void;
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: 'var(--spacing-2) var(--spacing-3)',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-brand-primary)' : '2px solid transparent',
        background: 'none',
        color: active ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

const DEFAULT_VALUES: CertificateFormValues = {
  cn: '',
  sni: '',
  expiresAt: '',
  fingerprint: '',
  certPem: '',
  keyPem: '',
  enabled: true,
};

export function CertificateFormDialog({ open, mode, initial, onClose, onSaved }: CertificateFormDialogProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'basic' | 'pem' | 'json'>('basic');
  const [toast, setToast] = useState<string | null>(null);

  const form = useForm<CertificateFormValues>({
    resolver: zodResolver(certificateFormSchema),
    defaultValues: initial
      ? {
          cn: initial.cn,
          sni: initial.sni ?? '',
          expiresAt: initial.expiresAt ?? '',
          fingerprint: initial.fingerprint ?? '',
          certPem: '',
          keyPem: '',
          enabled: initial.enabled,
        }
      : DEFAULT_VALUES,
  });

  const createCertificate = useCreateCertificate((cn) => {
    setToast(t('gateway.certificates.toast.created', { name: cn }));
    setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 1500);
  });
  const replaceCertificate = useReplaceCertificate(initial?.id ?? '', () => {
    setToast(t('gateway.certificates.toast.updated'));
    setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 1500);
  });

  useEffect(() => {
    if (open) {
      setActiveTab('basic');
      form.reset(initial
        ? {
            cn: initial.cn,
            sni: initial.sni ?? '',
            expiresAt: initial.expiresAt ?? '',
            fingerprint: initial.fingerprint ?? '',
            certPem: '',
            keyPem: '',
            enabled: initial.enabled,
          }
        : DEFAULT_VALUES);
    }
  }, [open, initial, form]);

  const watchedValues = form.watch();
  const isPending = createCertificate.isPending || replaceCertificate.isPending;

  // Exclude key from JSON preview for security
  const safePreview = { ...watchedValues, keyPem: watchedValues.keyPem ? '*** (masked)' : '' };

  const onSubmit = form.handleSubmit((values) => {
    if (mode === 'create') {
      createCertificate.mutate(values);
    } else if (initial) {
      replaceCertificate.mutate(values);
    }
  });

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%',
    padding: 'var(--spacing-2) var(--spacing-3)',
    border: `1px solid ${hasError ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    boxSizing: 'border-box',
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    marginBottom: 'var(--spacing-1)',
  };

  const fieldStyle: React.CSSProperties = { marginBottom: 'var(--spacing-3)' };

  return (
    <FormDialog
      open={open}
      title={mode === 'create' ? t('gateway.certificates.form.create') : t('gateway.certificates.form.edit')}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={isPending ? t('gateway.shared.actions.saving') : t('gateway.shared.actions.save')}
      cancelLabel={t('gateway.shared.actions.cancel')}
    >
      {toast && (
        <div role="alert" style={{ marginBottom: 'var(--spacing-3)', padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-success)', color: 'var(--color-text-inverse)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)' }}>
          {toast}
        </div>
      )}

      {/* Tabs */}
      <div role="tablist" style={{ display: 'flex', borderBottom: '1px solid var(--color-surface-border)', marginBottom: 'var(--spacing-4)' }}>
        <TabButton active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>{t('gateway.certificates.form.tabs.basic')}</TabButton>
        <TabButton active={activeTab === 'pem'} onClick={() => setActiveTab('pem')}>{t('gateway.certificates.form.tabs.pem')}</TabButton>
        <TabButton active={activeTab === 'json'} onClick={() => setActiveTab('json')}>JSON</TabButton>
      </div>

      {/* Basic Tab */}
      {activeTab === 'basic' && (
        <div role="tabpanel">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-3)' }}>
            <div style={{ gridColumn: '1 / -1', ...fieldStyle }}>
              <label htmlFor="cert-cn" style={labelStyle}>{t('gateway.certificates.form.cn.label')}</label>
              <input
                id="cert-cn"
                {...form.register('cn')}
                placeholder="*.example.com"
                aria-invalid={!!form.formState.errors.cn}
                aria-describedby={form.formState.errors.cn ? 'cert-cn-error' : undefined}
                style={inputStyle(!!form.formState.errors.cn)}
              />
              {form.formState.errors.cn && (
                <span id="cert-cn-error" role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                  {form.formState.errors.cn.message}
                </span>
              )}
            </div>
            <div style={fieldStyle}>
              <label htmlFor="cert-sni" style={labelStyle}>{t('gateway.certificates.form.sni.label')}</label>
              <input id="cert-sni" {...form.register('sni')} placeholder="api.example.com" style={inputStyle(false)} />
            </div>
            <div style={fieldStyle}>
              <label htmlFor="cert-expiresAt" style={labelStyle}>{t('gateway.certificates.form.expiresAt.label')}</label>
              <input id="cert-expiresAt" type="datetime-local" {...form.register('expiresAt')} style={inputStyle(false)} />
            </div>
            <div style={{ gridColumn: '1 / -1', ...fieldStyle }}>
              <label htmlFor="cert-fingerprint" style={labelStyle}>{t('gateway.certificates.form.fingerprint.label')}</label>
              <input
                id="cert-fingerprint"
                {...form.register('fingerprint')}
                placeholder="SHA256:..."
                style={{ ...inputStyle(false), fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1', ...fieldStyle }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)' }}>
                <input type="checkbox" {...form.register('enabled')} />
                {t('gateway.certificates.form.enabled.label')}
              </label>
            </div>
          </div>
        </div>
      )}

      {/* PEM Keys Tab */}
      {activeTab === 'pem' && (
        <div role="tabpanel">
          <div style={fieldStyle}>
            <label htmlFor="cert-certPem" style={labelStyle}>{t('gateway.certificates.form.certPem.label')}</label>
            <textarea
              id="cert-certPem"
              {...form.register('certPem')}
              placeholder={t('gateway.certificates.form.certPem.placeholder')}
              rows={6}
              style={{
                ...inputStyle(false),
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                resize: 'vertical',
              }}
            />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="cert-keyPem" style={labelStyle}>{t('gateway.certificates.form.keyPem.label')}</label>
            <textarea
              id="cert-keyPem"
              {...form.register('keyPem')}
              placeholder={t('gateway.certificates.form.keyPem.placeholder')}
              rows={6}
              autoComplete="off"
              aria-describedby="cert-keyPem-security"
              style={{
                ...inputStyle(false),
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                resize: 'vertical',
              }}
            />
            <p id="cert-keyPem-security" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-1)' }}>
              {t('gateway.certificates.form.keyPem.securityNote')}
            </p>
          </div>
        </div>
      )}

      {/* JSON Tab — key is masked */}
      {activeTab === 'json' && (
        <div role="tabpanel">
          <JsonEditor value={safePreview} readOnly height={280} ariaLabel={t('gateway.shared.json.preview')} />
        </div>
      )}
    </FormDialog>
  );
}
